import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { PlanogramLevel } from '../models/planogram-level';
import { PlanogramStatusType, PlanogramStatus } from '../models/planogram-status';
import { PlanogramOptions } from "../models/planogram-options";
import { PlanogramConstants } from '../constants';
import { PlanogramShare } from '../models/planogram-share';

/**
 * Primitive shapes the settings map is allowed to hold. Server round-trips
 * everything as a string; callers may store numbers or booleans which the
 * settings-payload builder coerces back to string at the wire boundary.
 */
export type SettingValue = string | number | boolean | null;

@Injectable({
    providedIn: 'root'
})
export class PlanogramService {
    private dataLoadId: string = '';
    private storeGuid: string = '';
    private storeName: string = '';
    private merchId: number = -1;
    private merchName: string = '';
    private stateId: string | null = null;
    private settings: Map<string, SettingValue> = new Map<string, SettingValue>();
    private planogramId: string | null = null;
    private isLinked: boolean = false;
    private share: PlanogramShare | null = null;
    private salesPeriodStart: Date | null = null;
    private salesPeriodEnd: Date | null = null;
    private targetDrops: number = 1;
    private levels: PlanogramLevel[] = [];
    private statusSubject: Subject<PlanogramStatus> = new Subject<PlanogramStatus>();
    private shelfSettingsSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);

    public currentStatus: PlanogramStatus = {
        state: PlanogramStatusType.INITIALIZING,
        lastUpdated: null,
        errorMessage: ''
    };
    public status$: Observable<PlanogramStatus> = this.statusSubject.asObservable();
    public startGeneratingAfterLoaded: boolean = false;
    public shelfSettings$ = this.shelfSettingsSubject.asObservable();

    configureFromParams(planogramId: string, stateId: string | null, options: PlanogramOptions) {
        this.planogramId = planogramId;
        this.stateId = stateId;
        this.isLinked = options.isLinked ?? false;
        this.storeGuid = options.storeGuid;
        this.storeName = options.storeName;
        this.merchId = options.merchId;
        this.merchName = options.merchName;
        this.share = options.share ?? null;
        this.salesPeriodStart = options.salesPeriodStart;
        this.salesPeriodEnd = options.salesPeriodEnd;
        this.applyOptions(options);
    }

    private applyOptions(options: PlanogramOptions) {
        this.setTargetDrops(options.targetDrops);
        this.setLevels(options.levels);
        this.setSettings(options.settings);
        this.updateStatusType(PlanogramStatusType.LOADED);
    }

    clearPlanogram() {
        this.updateStatusType(PlanogramStatusType.INITIALIZING);
        this.planogramId = null;
        this.isLinked = false;
        this.share = null;
        this.levels = [];
        this.settings = new Map<string, SettingValue>();
        this.storeGuid = '';
        this.storeName = '';
        this.merchId = -1;
        this.merchName = '';
        this.stateId = null;
    }

    getShare() {
        return this.share;
    }

    /** True when the loaded planogram is a linked (view-only) planogram. */
    isLinkedPlanogram(): boolean {
        return this.isLinked;
    }

    getSalesPeriodStart(): Date | null {
        return this.salesPeriodStart;
    }

    getSalesPeriodEnd(): Date | null {
        return this.salesPeriodEnd;
    }

    getPlanogramState(){
        return this.stateId;
    }

    setPlanogramState(stateId: string){
        this.stateId = stateId;
    }

    getDataLoad() {
        return this.dataLoadId;
    }

    setDataLoad(dataLoadId: string) {
        this.dataLoadId = dataLoadId;
    }

    getStatus(): PlanogramStatus | null {
        return this.currentStatus;
    }

    updateStatus(status: PlanogramStatus) {
        this.currentStatus = status;
        this.statusSubject.next(status);
    }

    updateStatusType(statusType: PlanogramStatusType) {
        if (this.currentStatus == null) { return; }
        this.currentStatus.state = statusType;
        this.statusSubject.next(this.currentStatus);
    }

    getSettings(): Map<string, SettingValue> {
        return this.settings;
    }

    setSettings(settings: Record<string, SettingValue> | null | undefined) {
        this.settings = new Map<string, SettingValue>();
        if (!settings) return;
        Object.keys(settings).forEach(key => {
            this.settings.set(key, settings[key]);
        });
    }

    // Returns `any` to keep call-site ergonomics: callers freely coerce or
    // truthy-default. Internal storage is typed (SettingValue); a future pass
    // can tighten this boundary once consumers are audited.
    getSetting(name: string): any {
        return this.settings.has(name) ? this.settings.get(name) : null;
    }

    setSetting(name: string, value: SettingValue) {
        this.settings.set(name, value);
    }

    /**
     * Serialize current settings for the wire — server stores everything as
     * strings. Objects are JSON-encoded; primitives are coerced. Replaces the
     * three near-identical copies that lived in PlanogramApiService.
     */
    buildSettingsPayload(): Record<string, string> {
        const payload: Record<string, string> = {};
        this.settings.forEach((value, key) => {
            // Drop unset/non-finite numeric settings instead of serializing them to the
            // strings "null"/"NaN", which round-trip back into NaN and break layout generation.
            if (value == null || (typeof value === 'number' && !Number.isFinite(value))) {
                return;
            }
            if (typeof value === 'object' && value !== null) {
                payload[key] = JSON.stringify(value);
            } else if (typeof value === 'string') {
                payload[key] = value;
            } else {
                payload[key] = `${value}`;
            }
        });
        return payload;
    }

    setDropHeight(dropHeight: number) {
        this.settings.set('drop-height', dropHeight);
    }

    getDropHeight(): number {
        const value = this.settings.get('drop-height');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return PlanogramConstants.DEFAULT_DROP_HEIGHT;
    }

    setDropDepth(dropDepth: number) {
        this.settings.set('drop-depth', dropDepth);
    }

    getDropDepth(): number {
        const value = this.settings.get('drop-depth');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return PlanogramConstants.DEFAULT_SHELF_DEPTH;
    }

    setDropFooterHeight(footerHeight: number) {
        this.settings.set('drop-footer-height', footerHeight);
    }

    getDropFooterHeight(): number {
        const value = this.settings.get('drop-footer-height');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return PlanogramConstants.DEFAULT_DROP_FOOTER_HEIGHT;
    }

    setShelfWidth(shelfWidth: number) {
        this.settings.set('shelf-width', shelfWidth);
    }

    getShelfWidth(): number {
        const value = this.settings.get('shelf-width');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return 91;
    }

    setShelfLipHeight(shelfLipHeight: number) {
        this.settings.set('shelf-lip-height', shelfLipHeight);
        this.shelfSettingsSubject.next();
    }

    getShelfLipHeight(): number {
        const value = this.settings.get('shelf-lip-height');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return 4;
    }

    getShelfMinimumSpacing() : number {
        const value = this.settings.get('shelf-min-spacing');
        if (value != null) {
            const parsed = parseInt(String(value));
            if (!isNaN(parsed)) return parsed;
        }
        return 2;
    }

    setShelfMinimumSpacing(shelfMinimumSpacing: number) {
        this.settings.set('shelf-min-spacing', shelfMinimumSpacing);
        this.shelfSettingsSubject.next();
    }

    setStoreAndMerch(storeGuid: string, merchId: number) {
        this.storeGuid = storeGuid;
        this.merchId = merchId;
    }

    setStoreAndMerchName(storeName: string, merchName: string) {
        this.storeName = storeName;
        this.merchName = merchName;
    }

    getStoreName() {
        return this.storeName;
    }

    getMerchName() {
        return this.merchName;
    }

    setTargetDrops(targetDrops: number){
        this.targetDrops = targetDrops;
    }

    getTargetDrops(useDefault: boolean = false): number {
        if (!useDefault && this.settings.has('target-drops')) {
            const val = this.settings.get('target-drops');
            if(val != null)
            {
                const parsed = Number(val);
                if (!isNaN(parsed)) {
                    return parsed;
                }
            }
        }
        return this.targetDrops;
    }

    setLevels(levels: PlanogramLevel[]) {
        this.levels = levels;
    }

    getPlanogram(): string | null {
        return this.planogramId;
    }

    getLevels(): PlanogramLevel[] {
        return this.levels;
    }

    getStoreGuid(): string {
        return this.storeGuid;
    }

    getMerchId(): number {
        return this.merchId;
    }
}
